import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInventoryStore } from "@presentation/stores/InventoryStore";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useShallow } from "zustand/react/shallow";
import {
  useNotifications,
  JobSettingsFormBlock,
  SoftwareSettingsFormBlock,
  useInjection,
  VersionSelectSettingsForm,
  NamingFormBlock,
  FastaFileFormBlock,
  RawFileFormBlock,
  JobPipelineSelectionBlock,
  LibraryFileFormBlock,
  ArchiveFileFormBlock,
  SoftwareSelectionFormBlock,
  QuantificationSelectionBlock,
} from "@presentation/components";
import { useLocation, useNavigate } from "react-router-dom";

import { JobPipelineTypeSchema } from "@domain/schemas/BI";
import { hasPipelineChoice, hasQuantificationChoice } from "@domain/software";
import { CreateJobService } from "@application/useCases";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import { extractProblemDetails, saveDuplicateJobMetadata } from "@presentation/common";
import { classifyDenial } from "@presentation/common/utils/denialClassifier";
import { PipelineStepEnumSchema } from "@domain/schemas/BI";
import { formatStepName } from "./JobList";
import { isSoftwareLicensed, useLicensedSoftware } from "./components/useLicensedSoftware";
import { getMissingRequirements } from "./jobRequirements";
import { MissingRequirementsSummary } from "./components/RequiredFieldIndicators";
import { useEffectiveProjectConfiguration } from "@presentation/components/helpers/useEffectiveProjectConfiguration";
import { usePermissionStore } from "@presentation/stores/PermissionStore";
import { useProjectPermission } from "@presentation/components/helpers/usePermissionCheck";
import { selectActiveProject, selectActiveTeam, useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useRecentlyVisitedStore } from "@presentation/stores/RecentlyVisitedStore";
import { useRecordProjectVisit } from "@presentation/hooks/useRecordProjectVisit";
import { toCreateJobPayload } from "@presentation/stores/JobSetupStore";
import type { CreateJobDraft } from "@presentation/stores/JobSetupStore";
import { CONTEXT_MANAGEMENT_ACTION_LOCKED_MESSAGE } from "@domain/valueObjects/contextManagementEntitlement";
import { ProjectActions } from "@domain/schemas/workspace";
import { LicenseRequiredMessage } from "@presentation/components/permission/LicenseRequiredMessage";

const normalizeVersion = (value: string) => value.trim().replace(/^(?:SN|SM)\s*/i, "");

type JobSetupFormProps = {
  onCreateButtonsBottomChange?: (bottomY: number) => void;
  isAdvancedSettingsExpanded: boolean;
  onAdvancedSettingsExpandedChange: (isExpanded: boolean) => void;
};

const JobSetupForm: React.FC<JobSetupFormProps> = ({ onCreateButtonsBottomChange, isAdvancedSettingsExpanded, onAdvancedSettingsExpandedChange }) => {
  // acquire the items needed from the store
  const {
    CreateJobState,
    resetCreateJobState,
    rerunAvailableSteps,
    resumeFromStepId,
    sourceSoftwareVersion,
    setResumeFromStepId,
    setOutputPath,
    setJobContext,
    setSoftwareVersion,
    setAllowCreationButton,
    setShowValidation,
    showValidation,
  } = useJobSetupStore(
    useShallow((state) => ({
      CreateJobState: state.CreateJobState,
      resetCreateJobState: state.resetCreateJobState,
      rerunAvailableSteps: state.rerunAvailableSteps,
      resumeFromStepId: state.CreateJobState.resumeFromStepId,
      sourceSoftwareVersion: state.sourceSoftwareVersion,
      setResumeFromStepId: state.setResumeFromStepId,
      setOutputPath: state.setOutputPath,
      setJobContext: state.setJobContext,
      setSoftwareVersion: state.setSoftwareVersion,
      setAllowCreationButton: state.setAllowCreationButton,
      setShowValidation: state.setShowValidation,
      showValidation: state.showValidation,
    })),
  );
  // Backend-resolved deployment/team/project software-version allow-list/default; see
  // `useEffectiveProjectConfiguration` for the fallback-walk order.
  const { activeTeamId, activeProjectId, workspaceStatus, workspaceProjects, setActiveTeamContext } = useWorkspaceContextStore(
    useShallow((state) => ({
      activeTeamId: state.activeTeamId,
      activeProjectId: state.activeProjectId,
      workspaceStatus: state.status,
      workspaceProjects: state.projects,
      setActiveTeamContext: state.setActiveTeamContext,
    })),
  );
  const currentTeam = useWorkspaceContextStore(selectActiveTeam) ?? null;
  const currentProject = useWorkspaceContextStore(selectActiveProject) ?? null;
  useRecordProjectVisit(currentProject?.teamId ?? "", currentProject?.id ?? "");
  const recentProjects = useRecentlyVisitedStore((state) => state.recentProjects);
  // Actively fetches (not just reads) this project's permissions - the page no longer gates
  // itself behind a route-level permission check, so this is the only thing populating the cache.
  const triggerJobsPermission = useProjectPermission(currentProject?.id ?? null, ProjectActions.TriggerJob, currentProject?.isDefault);
  const canTriggerJobs = triggerJobsPermission.allowed;
  const isPermissionPending = !triggerJobsPermission.allowed && triggerJobsPermission.reason === null && !!currentProject;
  const invalidatePermissions = usePermissionStore((state) => state.invalidate);
  // A denial the server returned for the current project, held so it can be shown
  // next to the project selection rather than only as a transient toast.
  const [projectDenial, setProjectDenial] = React.useState<ReturnType<typeof classifyDenial>>(null);

  // import the resetSelectedGridData from the InventoryStore
  const { resetSelectedGridData } = useInventoryStore(
    useShallow((state) => ({
      resetSelectedGridData: state.resetSelectedGridData,
    })),
  );

  // Dialog state + submitting flag
  const [visible, setVisible] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const actionSectionRef = React.useRef<HTMLFieldSetElement | null>(null);

  const { t: loc } = useTranslation();
  const { pushNotification } = useNotifications();

  // pre-setup the CreateJobService and inject it
  const createJobService = useInjection(CreateJobService);
  const injectedVersionsService = useInjection(ListSoftwareVersionsService);
  const versionsService = React.useRef(injectedVersionsService).current;
  const location = useLocation();
  const navigate = useNavigate();

  // The cluster is the source of truth for engine licensing, and it is also checked in rerun mode
  // where the engine picker is hidden but the job would still run on an unlicensed engine.
  const licenseState = useLicensedSoftware();
  const selectedSoftwareLicensed = isSoftwareLicensed(licenseState, CreateJobState.software);
  const rerunJobId = React.useMemo(() => new URLSearchParams(location.search).get("rerunJobId"), [location.search]);
  const isRerunMode = !!rerunJobId;
  const stepTypeToStableId: Record<PipelineStepEnumSchema, string> = React.useMemo(
    () => ({
      [PipelineStepEnumSchema.DownloadData]: "11111111-1111-1111-1111-111111111111",
      [PipelineStepEnumSchema.PulsarStep1]: "22222222-2222-2222-2222-222222222222",
      [PipelineStepEnumSchema.PulsarStep2]: "33333333-3333-3333-3333-333333333333",
      [PipelineStepEnumSchema.PulsarStep3]: "44444444-4444-4444-4444-444444444444",
      [PipelineStepEnumSchema.LibraryGeneration]: "55555555-5555-5555-5555-555555555555",
      [PipelineStepEnumSchema.DiaAnalysis]: "66666666-6666-6666-6666-666666666666",
      [PipelineStepEnumSchema.SneCombine]: "88888888-8888-8888-8888-888888888888",
      [PipelineStepEnumSchema.SneMerge]: "77777777-7777-7777-7777-777777777777",
      [PipelineStepEnumSchema.PsarMerging]: "99999999-9999-9999-9999-999999999999",
    }),
    [],
  );
  const rerunStepOptions = React.useMemo(
    () => rerunAvailableSteps.map((step) => ({ id: stepTypeToStableId[step.type as PipelineStepEnumSchema], label: `${formatStepName(step.type, CreateJobState.software)}` })),
    [rerunAvailableSteps, stepTypeToStableId, CreateJobState.software],
  );
  const currentTeamId = activeTeamId;
  const effectiveConfiguration = useEffectiveProjectConfiguration(currentProject?.id);
  const [candidateVersions, setCandidateVersions] = React.useState<string[]>([]);

  useEffect(() => {
    let isCancelled = false;

    const loadCandidateVersions = async () => {
      try {
        const versions = await versionsService.getCandidateVersionsListAsync();
        if (isCancelled) {
          return;
        }

        const normalized = [...new Set((versions ?? []).map((version) => normalizeVersion(version)).filter(Boolean))].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
        setCandidateVersions(normalized);
      } catch {
        if (!isCancelled) {
          setCandidateVersions([]);
        }
      }
    };

    loadCandidateVersions();

    return () => {
      isCancelled = true;
    };
  }, [versionsService]);

  const defaultSoftwareVersion = React.useMemo(() => {
    const configuredAllowedVersions = effectiveConfiguration.allowedSnVersions ?? [];
    const allowedVersionsSource = configuredAllowedVersions.length > 0 ? configuredAllowedVersions : candidateVersions;
    const allowedVersions = allowedVersionsSource
      .map((version) => normalizeVersion(version))
      .filter(Boolean)
      .toSorted((left, right) => right.localeCompare(left, undefined, { numeric: true }));
    const configuredDefault = normalizeVersion(effectiveConfiguration.defaultSnVersion ?? "");

    return configuredDefault && allowedVersions.includes(configuredDefault) ? configuredDefault : (allowedVersions[0] ?? "");
  }, [candidateVersions, effectiveConfiguration.allowedSnVersions, effectiveConfiguration.defaultSnVersion]);
  const selectedRerunStep = rerunStepOptions.find((step) => step.id === resumeFromStepId) ?? null;
  // An engine with a single pipeline has nothing to choose, so the selector is hidden.
  const showsPipelineChoice = hasPipelineChoice(CreateJobState.software);
  const showsQuantificationChoice = hasQuantificationChoice(CreateJobState.software);
  const isRerunWithArchiveSelection =
    isRerunMode && (resumeFromStepId === stepTypeToStableId[PipelineStepEnumSchema.PulsarStep1] || resumeFromStepId === stepTypeToStableId[PipelineStepEnumSchema.LibraryGeneration]);
  // No client-side path filtering: ListFastaService.listFastaAsync is already project-scoped
  // (honors every effective root, shared or private), so narrowing to a single prefix here would
  // hide valid files under any other configured root.
  // No backend file-name filter exists yet for FASTA files within the resolved root, only a path prefix.
  // A single place decides the copy so the JSX below stays a plain expression.
  const licenseBlockingMessage = React.useMemo(() => {
    if (selectedSoftwareLicensed) return null;
    switch (licenseState.status) {
      case "ready": {
        return <LicenseRequiredMessage software={CreateJobState.software} />;
      }
      case "error": {
        return "Could not read the search engine license status. Retrying before a job can be created.";
      }
      case "unknown": {
        return "Waiting for the cluster to report the search engine license before a job can be created.";
      }
      default: {
        return "Checking the search engine license before a job can be created.";
      }
    }
  }, [selectedSoftwareLicensed, licenseState.status, CreateJobState.software]);

  const missingRequirements = React.useMemo(() => getMissingRequirements(CreateJobState), [CreateJobState]);
  const missingSummaryRef = React.useRef<HTMLDivElement>(null);

  const handleSubmit = () => {
    // Validation is deferred to this point on purpose: the form stays neutral
    // until the user actually asks to create the job, and only then reports what
    // is outstanding. The button is left enabled so the reason is reachable —
    // a disabled button can state that something is wrong but never what.
    if (!selectedSoftwareLicensed || missingRequirements.length > 0) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setVisible(true);
  };

  // Landing here directly (not via a project's "Create job" button, which already set the active
  // context) with no active project needs a sensible default instead of leaving the form unusable:
  // the most recently visited project still in the caller's workspace, else the first project available.
  // Tracked so the permission-check effect below can tell "we picked this" from "the user picked this".
  const autoResolvedProjectIdRef = React.useRef<string | null>(null);
  // Armed as soon as there is ANY active project - whether already present on mount, or picked by
  // this auto-resolve - and never re-fires after that. Otherwise switching to a team with no
  // accessible project (a valid, deliberate choice; see the "Select a project in {team}" message
  // below) would re-empty `activeProjectId`, re-trigger this effect, and snap the context straight
  // back to some other team's project, undoing the switch the user just made.
  const hasAutoResolvedOnceRef = React.useRef(false);
  useEffect(() => {
    if (activeProjectId) {
      hasAutoResolvedOnceRef.current = true;
      return;
    }
    if (workspaceStatus !== "loaded" || hasAutoResolvedOnceRef.current || workspaceProjects.length === 0) return;
    const lastVisited = recentProjects.map((recent) => workspaceProjects.find((project) => project.id === recent.projectId)).find((project) => project !== undefined);
    const resolvedProject = lastVisited ?? workspaceProjects[0];
    if (!resolvedProject) return;
    hasAutoResolvedOnceRef.current = true;
    autoResolvedProjectIdRef.current = resolvedProject.id;
    setActiveTeamContext(resolvedProject.teamId, resolvedProject.id);
  }, [activeProjectId, workspaceStatus, workspaceProjects, recentProjects, setActiveTeamContext]);

  // The auto-resolved project might not be one the user can actually create jobs in (e.g. a Reader
  // role) - send them to pick a real one instead of stranding them on a form they cannot use.
  useEffect(() => {
    if (isPermissionPending || !currentProject || currentProject.id !== autoResolvedProjectIdRef.current) return;
    autoResolvedProjectIdRef.current = null;
    if (!canTriggerJobs && triggerJobsPermission.reason !== "entitlement") navigate("/projects");
  }, [currentProject, isPermissionPending, canTriggerJobs, triggerJobsPermission.reason, navigate]);

  useEffect(() => {
    if (!currentTeam) {
      return;
    }

    // Still a display-path convention, not a project link: the job's project is
    // now carried by the real projectId field on the payload.
    const defaultOutputPath = currentProject ? `${currentTeam.name}/${currentProject.name}` : currentTeam.name;

    if (!CreateJobState.outputPath?.trim() || CreateJobState.outputPath.startsWith(`${currentTeam.name}/`) || CreateJobState.outputPath === currentTeam.name) {
      setOutputPath(defaultOutputPath);
    }
  }, [CreateJobState.outputPath, currentProject, currentTeam, setOutputPath]);

  // A denial belongs to the project it was raised for; moving to another project
  // makes it meaningless and it must not linger over the new selection.
  useEffect(() => {
    setProjectDenial(null);
  }, [activeProjectId]);

  useEffect(() => {
    setJobContext(currentTeamId || null, currentProject?.id ?? null);
  }, [currentProject?.id, currentTeamId, setJobContext]);

  useEffect(() => {
    const selectedVersion = normalizeVersion(CreateJobState.softwareVersion ?? "");
    if (!selectedVersion && defaultSoftwareVersion) {
      setSoftwareVersion(defaultSoftwareVersion);
      return;
    }

    if (selectedVersion && (CreateJobState.softwareVersion ?? "") !== selectedVersion) {
      setSoftwareVersion(selectedVersion);
    }
  }, [CreateJobState.softwareVersion, defaultSoftwareVersion, setSoftwareVersion]);

  // The store update above only lands after this render commits, so scrolling/focusing here
  // would still see the pre-render ref. Doing it in an effect guarantees the summary exists.
  useEffect(() => {
    if (!showValidation || missingRequirements.length === 0) return;
    missingSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    missingSummaryRef.current?.focus();
  }, [showValidation, missingRequirements]);

  // function that will be run when user confirms the new job
  const handleDialogSubmit = async () => {
    if (submitting) return; // guard against double clicks

    // Resolve the project explicitly rather than letting the active context leak
    // in implicitly. In duplicate mode the source job's project is deliberately
    // not inherited: the user may be duplicating into a different context, and
    // silently reusing the original would create the job somewhere they cannot
    // see. The active project is the choice either way, and it is shown on the
    // form, so the choice is visible before submitting.
    const createJobPayload = toCreateJobPayload({ ...(CreateJobState as CreateJobDraft), projectId: currentProject?.id ?? CreateJobState.projectId ?? null });

    if (!createJobPayload) {
      setVisible(false);
      pushNotification("A job must belong to a project. Select a project before creating it.", "error", 10_000);
      return;
    }

    setSubmitting(true);
    setVisible(false); // ⟵ close the dialog immediately (optimistic close)

    try {
      const createdJob = await createJobService.createJobAsync(createJobPayload);
      const createdProjectId = createJobPayload.projectId;

      if (isRerunMode && rerunJobId && createdJob?.id) {
        saveDuplicateJobMetadata({
          jobId: createdJob.id,
          sourceJobId: rerunJobId,
          sourceJobName: CreateJobState.sourceJobName ?? "Unknown source job",
          sourceAnalysisName: CreateJobState.sourceAnalysisName ?? null,
          sourceCreatedAt: CreateJobState.sourceCreatedAt ?? null,
          sourceSoftwareVersion,
          duplicatedAt: new Date().toISOString(),
          restartFromStepId: CreateJobState.resumeFromStepId ?? null,
          reusedArtifacts: {
            rawFiles: (CreateJobState.rawFiles ?? []).map((file) => file.path),
            fastaFiles: (CreateJobState.fastaFiles ?? []).map((file) => file.path),
            libraryFiles: (CreateJobState.libraryFiles ?? []).map((file) => file.path),
            archiveFiles: (CreateJobState.archiveFiles ?? []).map((file) => file.path),
            softwareSettings: CreateJobState.softwareSettingsFile ? [CreateJobState.softwareSettingsFile.path] : [],
            conditionSetup: CreateJobState.conditionSetupFile ? [CreateJobState.conditionSetupFile.path] : [],
            modificationRepository: CreateJobState.modificationRepoFile ? [CreateJobState.modificationRepoFile.path] : [],
            reportSettings: (CreateJobState.reportSettingsFiles ?? []).map((file) => file.path),
            goFiles: (CreateJobState.goFiles ?? []).map((file) => file.path),
          },
        });
      }

      pushNotification(isRerunMode ? "Job duplicated and scheduled." : loc("newJobCreated"), "success", 10_000);
      const projectIdForReturn = createdProjectId ?? "";
      navigate(projectIdForReturn ? `/jobs?projectId=${encodeURIComponent(projectIdForReturn)}` : "/jobs");
    } catch (error: unknown) {
      const denial = classifyDenial(error);

      if (denial) {
        // Attribute the denial to the project rather than reporting it as a
        // generic submission failure: the user can act on "you cannot create
        // jobs in this project" by switching project, but not on "job creation
        // failed". The cached permissions clearly disagreed with the server, so
        // drop them and let the next read refetch.
        invalidatePermissions({ teamId: activeTeamId || undefined, projectId: createJobPayload.projectId });
        setProjectDenial(denial);
        const projectLabel = currentProject?.name ?? "this project";
        pushNotification(
          denial.kind === "entitlement-locked" ? `This deployment is not entitled to create jobs in ${projectLabel}.` : `You do not have permission to create jobs in ${projectLabel}.`,
          "error",
          10_000,
        );
        return;
      }

      const { title, detail, messages, status } = extractProblemDetails(error);
      const header = title || loc("jobCreationFailed");
      const statusPart = status ? ` [${status}]` : "";
      const lines: string[] = [];
      if (detail?.trim()) lines.push(detail.trim());
      if (messages.length > 0) lines.push(...messages.map((m) => m.trim()).filter(Boolean));

      const text = lines.length > 0 ? `${header}${statusPart}\n• ${lines.join("\n• ")}` : `${header}${statusPart}`;

      pushNotification(text, "error", 10_000);
    } finally {
      setSubmitting(false);
    }
  };

  // enable/disable Create Job button
  useEffect(() => {
    // The requirement list is the single source of truth for form completeness. The
    // license check stays separate: the engine must have a confirmed license in the
    // cluster, otherwise the job would be admitted here only to fail in the orchestrator.
    setAllowCreationButton(selectedSoftwareLicensed && getMissingRequirements(CreateJobState).length === 0);
  }, [CreateJobState, selectedSoftwareLicensed, setAllowCreationButton]);

  React.useLayoutEffect(() => {
    if (!onCreateButtonsBottomChange) {
      return;
    }

    const updateCreateButtonsBottom = () => {
      if (!actionSectionRef.current) {
        return;
      }

      const rect = actionSectionRef.current.getBoundingClientRect();
      onCreateButtonsBottomChange(rect.bottom);
    };

    updateCreateButtonsBottom();

    const resizeObserver = new ResizeObserver(() => {
      updateCreateButtonsBottom();
    });

    if (actionSectionRef.current) {
      resizeObserver.observe(actionSectionRef.current);
    }

    // Also observe the viewport root so bottom-position shifts caused purely by
    // window resizing (without the action section itself changing size) are caught.
    resizeObserver.observe(document.documentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [CreateJobState.pipelineType, isRerunMode, onCreateButtonsBottomChange, rerunStepOptions.length, canTriggerJobs]);

  // Once everything outstanding has been supplied the amber marks have nothing
  // left to point at, so they are cleared rather than left on screen.
  useEffect(() => {
    if (missingRequirements.length === 0) setShowValidation(false);
  }, [missingRequirements, setShowValidation]);

  // define the reset function. We will use this to reset the form
  const resetForm = () => {
    resetCreateJobState();
    // also reset the selectedGridData using resetSelectedGridData function from the InventoryStore
    resetSelectedGridData();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {visible && (
        <Dialog
          className=""
          title={
            isRerunMode
              ? "A new job will be created from this job configuration and scheduled to run. Do you wish to proceed?"
              : "The current job setup will be scheduled to run once you confirm. Do you wish to proceed?"
          }
          onClose={() => setVisible(false)} // ⟵ always closable
        >
          {/* <Checkbox value={redirectToJobList} onChange={handleRedirectToJobListChange} label="Auto-redirect to job list" /> */}
          <DialogActionsBar>
            <Button themeColor="primary" onClick={handleDialogSubmit} disabled={submitting}>
              {submitting ? "Scheduling..." : "Confirm"}
            </Button>
            <Button onClick={() => setVisible(false)} disabled={submitting}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      <div className="job-setup-form" style={{ flex: 1, overflowY: "auto", paddingLeft: "8px", paddingRight: "8px" }}>
        <Form
          onSubmit={handleSubmit}
          render={() => (
            <FormElement>
              {workspaceStatus === "loaded" && workspaceProjects.length === 0 && (
                <span role="alert" style={{ color: "#b91c1c", fontSize: "0.9em" }}>
                  You are not part of any project yet. Ask a team manager to add you to one before creating a job.
                </span>
              )}
              {workspaceStatus === "loaded" && workspaceProjects.length > 0 && !currentProject && (
                <span role="alert" style={{ color: "#b91c1c", fontSize: "0.9em" }}>
                  {currentTeam ? `Select a project in ${currentTeam.name} before creating a job.` : "Select a project before creating a job."}
                </span>
              )}
              {projectDenial && (
                <span role="alert" style={{ color: "#b91c1c", fontSize: "0.9em" }}>
                  {projectDenial.kind === "entitlement-locked" ? CONTEXT_MANAGEMENT_ACTION_LOCKED_MESSAGE : `You do not have permission to create jobs in ${currentProject?.name ?? "this project"}.`}
                </span>
              )}
              {!projectDenial && !canTriggerJobs && !isPermissionPending && currentProject && (
                <span style={{ color: "#b91c1c", fontSize: "0.9em" }}>
                  {triggerJobsPermission.reason === "entitlement" ? CONTEXT_MANAGEMENT_ACTION_LOCKED_MESSAGE : `You do not have permission to create jobs in ${currentProject.name}.`}
                </span>
              )}
              <NamingFormBlock />

              {!isRerunMode && <SoftwareSelectionFormBlock />}
              <VersionSelectSettingsForm />
              {!isRerunMode && showsPipelineChoice && <JobPipelineSelectionBlock />}
              {showsQuantificationChoice && <QuantificationSelectionBlock />}
              <SoftwareSettingsFormBlock />
              {isRerunMode && rerunStepOptions.length > 0 && (
                <fieldset className={"k-form-fieldset"}>
                  <legend className="k-form-legend">
                    <strong>Restart from:</strong>
                  </legend>
                  <div style={{ marginBottom: "10px" }}>
                    <DropDownList
                      style={{ width: "100%" }}
                      data={rerunStepOptions}
                      textField="label"
                      dataItemKey="id"
                      value={selectedRerunStep}
                      onChange={(event) => setResumeFromStepId(event.value?.id ?? null)}
                    />
                  </div>
                </fieldset>
              )}
              <RawFileFormBlock />
              {CreateJobState.pipelineType !== JobPipelineTypeSchema.LibraryBased && <FastaFileFormBlock />}
              {CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryBased && <LibraryFileFormBlock />}
              {(CreateJobState.pipelineType === JobPipelineTypeSchema.DirectDIA || CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryGeneration) &&
                (!isRerunMode || isRerunWithArchiveSelection) && <ArchiveFileFormBlock />}
              <JobSettingsFormBlock isAdvancedSettingsExpanded={isAdvancedSettingsExpanded} onAdvancedSettingsExpandedChange={onAdvancedSettingsExpandedChange} />

              <fieldset ref={actionSectionRef} className={"k-form-fieldset"}>
                {licenseBlockingMessage && (
                  <div role="alert" style={{ color: "#b45309", marginBottom: 8 }}>
                    {licenseBlockingMessage}
                  </div>
                )}
                <MissingRequirementsSummary ref={missingSummaryRef} missing={showValidation ? missingRequirements : []} />
                <div className="k-form-buttons">
                  <Button
                    type="submit"
                    themeColor={"primary"}
                    disabled={submitting || !canTriggerJobs || !currentTeam || !currentProject}
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                    onClick={handleSubmit}
                  >
                    {isRerunMode ? "Duplicate And Run" : "Create Job"}
                  </Button>
                  <Button type="reset" themeColor={"secondary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={resetForm} disabled={submitting}>
                    Reset
                  </Button>
                </div>
                <br />
              </fieldset>
            </FormElement>
          )}
        />
      </div>
    </div>
  );
};

export default JobSetupForm;
